import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { FlaskConical } from 'lucide-react';
import { beginMicrosoftLogin } from '@/lib/microsoftLogin';

const ENABLE_REGISTER = process.env.REACT_APP_ENABLE_REGISTER === 'true';
const ENABLE_PASSWORD_LOGIN = process.env.REACT_APP_ENABLE_PASSWORD_LOGIN === 'true';
const openMicrosoftLogin = url => window.location.assign(url);

function MicrosoftLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

export default function LoginPage({ onLogin, onMicrosoftLogin = openMicrosoftLogin }) {
  const [tab, setTab] = useState('login');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (error === 'domain_not_allowed') {
      toast.error('Only @germinalbiotech.com accounts are allowed.');
    } else if (error === 'not_preapproved') {
      toast.error('Your Microsoft account is not approved for Lab Operations. Ask an admin to add you first.');
    } else if (error === 'pending_approval') {
      toast.info('Your account exists but is currently inactive. Contact an administrator.');
    } else if (error === 'microsoft_login_binding' || error === 'microsoft_invalid_state') {
      toast.error('Microsoft sign-in could not be verified. Start sign-in again in this tab.');
    } else if (error) {
      toast.error(`Microsoft sign-in failed: ${error}`);
    }
    if (error) {
      params.delete('error'); params.delete('detail');
      const query = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : ''));
    }
  }, []);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', confirm_password: '', full_name: '' });
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.password !== registerData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: registerData.email,
          password: registerData.password,
          full_name: registerData.full_name,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Registration failed');
      toast.success('Account created! You can now log in.');
      setTab('login');
      setLoginData({ email: registerData.email, password: '' });
    } catch (error) {
      toast.error(error.message || 'Registration failed');
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
      e.preventDefault();
      setLoading(true);

      try {
        const formData = new URLSearchParams();
        formData.append('username', loginData.email);
        formData.append('password', loginData.password);

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formData.toString(),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.detail || 'Login failed');
        }

        // Fetch real user profile so role is accurate
        const meRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/me`, {
          headers: { Authorization: `Bearer ${data.access_token}` },
        });
        const me = await meRes.json();

        toast.success('Login successful');
        onLogin(data.access_token, me);

      } catch (error) {
        console.error("Login Error:", error);
        toast.error(error.message || 'Login failed');
      }
      setLoading(false);
    };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(/images/login-hero.png)' }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <FlaskConical size={48} className="mb-4" />
          <h1 className="text-4xl font-semibold heading mb-2">Lab Operations Suite</h1>
          <p className="text-lg opacity-90">Streamline your laboratory workflows from protocols to procurement</p>
        </div>
      </div>

      {/* Right side - Login / Register */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-semibold heading mb-2">
              {tab === 'login' ? 'Welcome Back' : 'Create Account'}
            </h2>
            <p className="text-muted-foreground">
              {tab === 'login' ? 'Sign in to Lab Operations Suite' : 'Register with your @germinalbiotech.com email'}
            </p>
          </div>

          {/* Microsoft SSO button */}
          <button
            type="button"
            onClick={async () => {
              try {
                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/microsoft/status`);
                const data = await res.json();
                if (!data.configured) {
                  toast.error('Microsoft SSO is not configured yet. Contact your administrator.');
                  return;
                }
              } catch {
                // If status check fails, try anyway
              }
              try {
                const binding = beginMicrosoftLogin();
                onMicrosoftLogin(`${process.env.REACT_APP_BACKEND_URL}/api/auth/microsoft/login?login_binding=${encodeURIComponent(binding)}`);
              } catch {
                toast.error('Allow browser session storage, then try Microsoft sign-in again.');
              }
            }}
            className="w-full flex items-center justify-center gap-3 border border-border rounded-lg px-4 py-2.5 hover:bg-accent/50 transition-colors font-medium text-sm mb-4"
          >
            <MicrosoftLogo />
            Sign in with Microsoft
          </button>

          {ENABLE_PASSWORD_LOGIN ? (
            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center text-xs text-muted-foreground"><span className="bg-background px-2">or sign in with email</span></div>
            </div>
          ) : null}

          {/* Tab switcher — Register tab only shown when explicitly enabled */}
          {ENABLE_PASSWORD_LOGIN && ENABLE_REGISTER ? (
            <div className="flex border border-border rounded-lg p-1 mb-6 bg-muted/40">
              <button
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${tab === 'login' ? 'bg-background shadow text-foreground' : 'text-muted-foreground'}`}
                onClick={() => setTab('login')}
              >
                Sign In
              </button>
              <button
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${tab === 'register' ? 'bg-background shadow text-foreground' : 'text-muted-foreground'}`}
                onClick={() => setTab('register')}
              >
                Register
              </button>
            </div>
          ) : null}

          {ENABLE_PASSWORD_LOGIN && (tab === 'login' || !ENABLE_REGISTER) ? (
            <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="you@germinalbiotech.com"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <Label htmlFor="login-password">Password</Label>
                <Input
                  id="login-password"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  data-testid="login-password-input"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          ) : ENABLE_PASSWORD_LOGIN ? (
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <Label htmlFor="reg-name">Full Name</Label>
                <Input
                  id="reg-name"
                  type="text"
                  placeholder="Jane Smith"
                  value={registerData.full_name}
                  onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="reg-email">Email</Label>
                <Input
                  id="reg-email"
                  type="email"
                  placeholder="you@germinalbiotech.com"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="reg-password">Password</Label>
                <Input
                  id="reg-password"
                  type="password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="reg-confirm">Confirm Password</Label>
                <Input
                  id="reg-confirm"
                  type="password"
                  value={registerData.confirm_password}
                  onChange={(e) => setRegisterData({ ...registerData, confirm_password: e.target.value })}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Only @germinalbiotech.com email addresses are accepted.
                Your account will be reviewed by an administrator.
              </p>
            </form>
          ) : (
            <p className="text-xs text-center text-muted-foreground">
              Access is restricted to approved Germinal Biotech Microsoft 365 accounts.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
